import { makeStyles, styled, ThemeProvider } from '@material-ui/styles'
import { RootState } from 'app/store'
import clsx from 'clsx'
import { CacheState, cacheStoreKey, lastRefreshTimeStoreKey } from 'core/caching/cacheReducers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useToggler from 'core/hooks/useToggler'
import Theme from 'core/themes/model'
import { useCustomTheme } from 'core/themes/ThemeManager'
import DataKeys from 'k8s/DataKeys'
import moment from 'moment'
import { equals, prop } from 'ramda'
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { ensureArray } from 'utils/fp'
import generateTestId from 'utils/test-helpers'
import { Align } from '../tooltip/Tooltip'
import GridColumnsPopover from './GridColumnsPopover'
import { GridFilteringProps } from './hooks/useGridFiltering'
import { GridManagedColumnsProps } from './hooks/useGridManagedColumns'
import { GridBatchActionGroupProps, GridBatchActionsProps } from './hooks/useGridSelectableRows'

interface GridToolbarProps<
  T,
  GF extends Record<string, unknown>,
  F extends Record<string, unknown>,
  DF extends Record<string, unknown>
> extends GridFilteringProps<GF, F, DF>, GridBatchActionsProps<T>, GridManagedColumnsProps {
  compact?: boolean
  label?: string
  onRefresh?: () => void | Promise<void>
  extraToolbarContent?: ReactNode
  ToolbarContainer?: FC<PropsWithChildren<{ className?: string; selectedCount?: number }>>
  showItemsCountInLabel?: boolean
  itemsCount?: number
  tooltip?: ReactNode
  align?: Align
  isReadOnly?: boolean
  isBackgroundRefresh?: boolean
  dataKey?: DataKeys
}

const useStyles = makeStyles<Theme>((theme) => ({
  gridToolbar: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    // color: ({ selectedCount }) =>
    //   selectedCount
    //     ? theme.components.table.activeToolbarColor
    //     : theme.components.table.toolbarColor,
    color: theme.components.table.toolbarColor,
    // backgroundColor: ({ selectedCount }) =>
    //   selectedCount ? theme.components.table.activeToolbar : theme.components.table.toolbar,
    backgroundColor: theme.components.table.toolbar,
    transitionTimingFunction: 'ease-in',
    transition: 'background-color .2s ease',
    border: 0,
    borderRadius: '4px 4px 0 0',
    padding: '8px 16px 8px 16px',
    minHeight: 56,
    borderBottom: `1px solid ${theme.components.table.border}`,
    boxSizing: 'border-box',
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  label: {
    ...theme.typography.subtitle2,
    color: 'inherit',
    marginRight: theme.spacing(3),
  },
  selectedCount: {
    color: 'inherit',
  },
  clearBtn: {
    cursor: 'pointer',
    color: theme.components.table.toolbarPassiveColor,
  },
  verticalLine: {
    borderLeft: `1px solid ${theme.components.typography.default}`,
    width: 1,
    height: 34,
    margin: theme.spacing(0, 1),
  },
  batchActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    gap: 12,
  },
  dropdownBatchActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 16,
    marginRight: 32,
  },
  tools: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'end',
    gap: 16,
  },
  buttons: {
    display: 'grid',
    gridAutoFlow: 'column',
    marginRight: theme.spacing(1),
  },
  button: {
    ...theme.typography.inputTable,
    display: 'grid',
    gridAutoFlow: 'column',
    cursor: 'pointer',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: 4,
    gap: 8,
    '&:hover': {
      backgroundColor: theme.components.table.hoverBackground,
    },
  },
  extraContent: {
    marginLeft: theme.spacing(1),
  },
  tooltip: {
    display: 'inline-block',
    marginLeft: 8,
  },
  refreshIcon: {
    color: theme.components.table.toolbarColor,
    marginLeft: theme.spacing(1.5),
    fontWeight: 500,
  },
  actionDropdown: {
    position: 'relative',
    height: 36,
  },
  dropdownLabel: {
    display: 'inline-grid',
    gap: 8,
    gridTemplateColumns: 'repeat(3, max-content)',
    alignItems: 'center',
    cursor: 'pointer',
  },
  additionalDropdownLabelsStyles: {
    padding: '8px 12px',
    border: `solid 1px ${theme.palette.grey[200]}`,
    borderRadius: 4,
    background: theme.components.dropdown.background,
  },
  dropdownMenu: {
    display: 'grid',
    background: theme.components.dropdown.background, // Bc toolbar uses opposite mode, use the opposite colors
    position: 'absolute',
    zIndex: 100,
    top: 32,
    right: 0,
    borderRadius: 4,
    border: `solid 1px ${theme.components.dropdown.border}`,
  },
  additionalDropdownMenuStyles: {
    top: 42,
  },
  filterBar: {
    transition: 'height .2s ease',
    height: 'max-content',
    overflow: 'visible',
    padding: '16px',
    background: theme.components.table.hoverBackground,
    display: 'grid',
    gap: 16,
  },
  filterBarClosed: {
    height: 0,
    overflow: 'hidden',
    padding: '0px',
  },
  filterDropdowns: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    justifyContent: 'left',
  },
  activeFilters: {
    display: 'flex',
    gap: 16,
  },
  activeFilterBox: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gap: 8,
    alignItems: 'center',
    padding: '4px 12px',
    background: theme.components.table.background,
    border: `1px solid ${theme.components.table.border}`,
  },
  activeFilterText: {
    fontSize: 12,
  },
  withIcon: {
    display: 'grid',
    gridTemplateColumns: '16px auto',
    gap: 12,
    alignItems: 'center',
  },
  strong: {
    fontWeight: 800,
  },
  lastUpdatedText: {
    display: 'inline',
    marginLeft: 12,
  },
  batchActionWrapper: {
    cursor: 'pointer',
  },
  batchActionWrapperDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
}))

const DefaultToolbarContainer: FC<PropsWithChildren<{
  selectedCount?: number
  className?: string
}>> = ({ children, selectedCount, ...props }) => {
  const [theme] = useCustomTheme()
  // return (
  //   <ThemeProvider theme={darkTheme}>
  //     <div {...props}>{children}</div>
  //   </ThemeProvider>
  // )
  // return (
  //   <ThemeProvider theme={selectedCount ? darkTheme : theme}>
  //     <div {...props}>{children}</div>
  //   </ThemeProvider>
  // )
  return (
    <ThemeProvider theme={theme}>
      <div {...props}>{children}</div>
    </ThemeProvider>
  )
}

// Consider moving to a helper file
// Detecting clicks outside the dropdown
const clickOutside = (ref, onClickOutside) => {
  useEffect(() => {
    const modalRef = document.getElementById('modal-portal-root')
    const handleClickOutside = (event) => {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !modalRef.contains(event.target) &&
        !document.body.classList.contains('blur') // Added this check to resolve https://platform9.atlassian.net/browse/PCD-4153
      ) {
        onClickOutside()
      }
    }
    // Bind
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // dispose
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, onClickOutside])
}

export function BatchActionDropdown<T>({
  actions,
  icon,
  label = 'Other Actions',
  renderOutsideGrid = false,
}: GridBatchActionGroupProps<T>) {
  // TODO: Add a click handler for outside of the element
  const classes = useStyles({})
  const [isOpen, setOpen] = useState(false)

  const wrapperRef = useRef(null)
  clickOutside(wrapperRef, () => {
    setOpen(false)
  })

  return (
    <div className={classes.actionDropdown} ref={wrapperRef}>
      <div
        className={clsx(
          classes.dropdownLabel,
          renderOutsideGrid && classes.additionalDropdownLabelsStyles,
        )}
        onClick={() => setOpen(!isOpen)}
      >
        <FontAwesomeIcon>{icon}</FontAwesomeIcon>
        <Text variant={'body2'} className={clsx(renderOutsideGrid && classes.strong)}>
          {label}
        </Text>
        <FontAwesomeIcon solid size="sm">
          {isOpen ? 'caret-up' : 'caret-down'}
        </FontAwesomeIcon>
      </div>
      {isOpen && (
        <div
          className={clsx(
            classes.dropdownMenu,
            renderOutsideGrid && classes.additionalDropdownMenuStyles,
          )}
        >
          {actions?.map(
            ({
              key,
              label,
              triggerAction,
              tooltip,
              align,
              BatchActionButton,
              icon,
              disabled,
              hideIfDisabled,
              ...props
            }) => {
              if (disabled && hideIfDisabled) {
                return null
              }

              return (
                <BatchActionButton
                  {...props}
                  key={key}
                  disabled={disabled}
                  onClick={triggerAction}
                  closeDropdown={() => setOpen(false)}
                >
                  <Tooltip className={classes.tooltip} message={tooltip || ''} align={align}>
                    <div className={classes.withIcon}>
                      {icon && <FontAwesomeIcon noBaseClass>{icon}</FontAwesomeIcon>}
                      {label}
                    </div>
                  </Tooltip>
                </BatchActionButton>
              )
            },
          )}
        </div>
      )}
    </div>
  )
}

const GridRefreshButton = styled(({ className, onRefresh }) => {
  return (
    <Text
      data-testid={generateTestId('refresh')}
      noWrap
      onClick={onRefresh}
      component="div"
      className={className}
    >
      <FontAwesomeIcon>sync-alt</FontAwesomeIcon>
      Refresh
    </Text>
  )
})<Theme>(({ theme }) => ({
  ...theme.typography.inputTable,
  display: 'grid',
  gridAutoFlow: 'column',
  cursor: 'pointer',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: 4,
  gap: 8,
  '&:hover': {
    backgroundColor: theme.components.table.hoverBackground,
  },
}))

export default function GridToolbar<
  T,
  GF extends Record<string, unknown>,
  F extends Record<string, unknown>,
  DF extends Record<string, unknown>
>(props: GridToolbarProps<T, GF, F, DF>) {
  const [isOpen, toggleIsOpen] = useToggler(false)
  const classes = useStyles()
  const {
    label,
    columns,
    columnTogglers,
    selectedCount,
    batchActions,
    dropdownBatchActions,
    globalFilters,
    filters,
    dropdownFilters,
    dropdownFilterValues,
    dropdownValuesByKey,
    onRefresh,
    extraToolbarContent,
    clearSelectedRows,
    multiSelectionEnabled,
    ToolbarContainer = DefaultToolbarContainer,
    columnHidingDisabled,
    showItemsCountInLabel = true,
    itemsCount = undefined,
    tooltip = undefined,
    isReadOnly,
    isBackgroundRefresh = false,
    dataKey,
  } = props
  const removeFilterValue = useCallback(
    (filterValue) => {
      const keyValues = ensureArray(dropdownValuesByKey[filterValue.key])
      const filteredKeyValues = keyValues.filter((val) => {
        return !equals(val, filterValue.value)
      })
      filterValue.updateFilterValue(filteredKeyValues)
    },
    [dropdownValuesByKey],
  )
  const cache = useSelector<RootState, CacheState>(prop(cacheStoreKey))

  const timestamp = useMemo(() => {
    const cacheTimestamp = cache?.[lastRefreshTimeStoreKey]?.[dataKey]
    if (!dataKey || !cacheTimestamp) {
      return ''
    }
    const timeChecked = moment(cacheTimestamp).format('h:mm:ss A')
    return timeChecked
  }, [dataKey, cache[lastRefreshTimeStoreKey]])

  return (
    <>
      <ToolbarContainer selectedCount={selectedCount} className={classes.gridToolbar}>
        <div className={classes.batchActions}>
          <Text
            data-testid={generateTestId(label, 'label')}
            className={classes.label}
            variant="subtitle2"
            component="p"
          >
            {showItemsCountInLabel && itemsCount ? `${label} (${itemsCount})` : label}
            {tooltip && (
              <Tooltip className={classes.tooltip} message={tooltip}>
                <FontAwesomeIcon>info-circle</FontAwesomeIcon>
              </Tooltip>
            )}
            {isBackgroundRefresh && (
              <Tooltip className={classes.tooltip} message="Refreshing">
                <FontAwesomeIcon className={classes.refreshIcon} spin size="lg">
                  refresh
                </FontAwesomeIcon>
              </Tooltip>
            )}
            {timestamp && !selectedCount && (
              <Text variant="body2" className={classes.lastUpdatedText}>
                Last Updated {timestamp}
              </Text>
            )}
          </Text>
          {selectedCount ? (
            <>
              {multiSelectionEnabled ? (
                <>
                  <Text
                    data-testid={generateTestId('selected')}
                    variant="body2"
                    className={classes.selectedCount}
                    component="p"
                  >
                    {`${selectedCount} Selected`}
                  </Text>
                  <Text
                    data-testid={generateTestId('clear', 'all')}
                    variant="body2"
                    className={classes.clearBtn}
                    component="p"
                    onClick={clearSelectedRows}
                  >
                    {`Clear All`}
                  </Text>
                  <div className={classes.verticalLine} />
                </>
              ) : null}
              {!isReadOnly &&
                batchActions?.map(({ key, label, triggerAction, BatchActionButton, ...props }) => (
                  <div
                    key={key}
                    className={
                      props.disabled
                        ? classes.batchActionWrapperDisabled // Only style disabled look
                        : classes.batchActionWrapper
                    }
                    // Always allow pointer events so tooltip works
                    onClick={(e) => {
                      if (props.disabled) {
                        e.stopPropagation()
                        return
                      }
                      triggerAction()
                    }}
                  >
                    <BatchActionButton {...props}>{label}</BatchActionButton>
                  </div>
                ))}
              {!!dropdownBatchActions?.length && !isReadOnly && (
                <div className={classes.dropdownBatchActions}>
                  {dropdownBatchActions.map(({ icon, label, actions }) => (
                    <BatchActionDropdown<T>
                      key={label}
                      icon={icon}
                      label={label}
                      actions={actions}
                    />
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
        <div data-testid={generateTestId('search')} className={classes.tools}>
          {globalFilters.map(
            ({
              key,
              label,
              filterValue,
              filterValues,
              updateFilterValue,
              FilterComponent,
              ...rest
            }) => (
              <FilterComponent
                key={String(key)}
                value={filterValue}
                filterValues={filterValues}
                onChange={updateFilterValue}
                label={label}
                {...rest}
              />
            ),
          )}

          <div className={classes.buttons}>
            {!columnHidingDisabled && (
              <GridColumnsPopover columns={columns} columnTogglers={columnTogglers} />
            )}
            {onRefresh && <GridRefreshButton onRefresh={onRefresh} />}
            {!!dropdownFilters?.length && (
              <Text noWrap component="div" className={classes.button} onClick={toggleIsOpen}>
                <FontAwesomeIcon>filter</FontAwesomeIcon>
                Filters
                <FontAwesomeIcon>{isOpen ? 'angle-up' : 'angle-down'}</FontAwesomeIcon>
              </Text>
            )}
          </div>

          {filters.map(
            ({ key, filterValue, filterValues, updateFilterValue, FilterComponent, ...rest }) => (
              <FilterComponent
                key={String(key)}
                value={filterValue}
                filterValues={filterValues}
                onChange={updateFilterValue}
                {...rest}
              />
            ),
          )}
          {extraToolbarContent ? (
            <div className={classes.extraContent}>{extraToolbarContent}</div>
          ) : null}
        </div>
      </ToolbarContainer>
      <div
        className={clsx(classes.filterBar, {
          [classes.filterBarClosed]: !isOpen,
        })}
      >
        <div className={classes.filterDropdowns}>
          {dropdownFilters?.map(
            ({ key, filterValue, updateFilterValue, FilterComponent, filterComponentProps }) => (
              <FilterComponent
                key={key}
                value={filterValue}
                onChange={updateFilterValue}
                {...filterComponentProps}
              />
            ),
          )}
        </div>
        {!!dropdownFilterValues?.length && (
          <div className={classes.activeFilters}>
            {dropdownFilterValues?.map((filterInfo) => (
              <div key={filterInfo?.display} className={classes.activeFilterBox}>
                <Text variant="body2" className={classes.activeFilterText}>
                  <b>{filterInfo?.label}:</b> {filterInfo?.display}
                </Text>
                <FontAwesomeIcon
                  onClick={() => {
                    removeFilterValue(filterInfo)
                  }}
                  size="md"
                >
                  xmark
                </FontAwesomeIcon>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
